'use client'

import Link from 'next/link'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import type { WordPressSeoDetectionResult } from 'sprites-types/api/v2/integrations'
import Modal from '../Modal/Modal'
import { Button } from '../ui/button'
import { Spinner } from '../ui/spinner'

interface WordPressSeoSetupModalProps {
  credentials: {
    siteUrl: string
    username: string
    applicationPassword: string
  }
  onComplete: (enabledSeo: boolean) => void
  onSkip: () => void
}

type Step = 'confirm' | 'detecting' | 'results' | 'installing' | 'wizard_guidance' | 'complete'

export function WordPressSeoSetupModal({
  credentials,
  onComplete,
  onSkip,
}: WordPressSeoSetupModalProps) {
  const [step, setStep] = useState<Step>('confirm')
  const [detection, setDetection] = useState<WordPressSeoDetectionResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Backend normalizes the URL, but we need a clean base for constructing admin links
  const baseUrl = credentials.siteUrl.replace(/\/$/, '')

  const detectSetup = useCallback(async () => {
    console.log('[SEO Setup] Starting detection with credentials:', {
      siteUrl: credentials.siteUrl,
      username: credentials.username,
      hasPassword: !!credentials.applicationPassword,
    })

    setStep('detecting')
    setError(null)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/wordpress/seo-setup/detect`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        }
      )

      console.log('[SEO Setup] Response status:', response.status)
      const data = await response.json()
      console.log('[SEO Setup] Response data:', data)

      if (!data.success) {
        console.error('[SEO Setup] Detection failed:', data.error)
        setError(data.error || 'Failed to detect SEO setup')
        setStep('results')
        return
      }

      setDetection(data.result)
      setStep('results')
    } catch (err) {
      console.error('[SEO Setup] Detection error:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      setStep('results')
    }
  }, [credentials])

  const installRankMath = useCallback(async () => {
    setStep('installing')
    setError(null)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/wordpress/seo-setup/install`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...credentials,
            deactivateConflicts: true,
          }),
        }
      )

      const data = await response.json()

      if (!data.success) {
        setError(data.error || 'Failed to install Rank Math')
        setStep('results')
        return
      }

      toast.success('Rank Math installed successfully!')

      // Show wizard guidance step after fresh install
      setStep('wizard_guidance')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      setStep('results')
    }
  }, [credentials, detectSetup])

  if (step === 'confirm') {
    return (
      <Modal isOpen={true}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            <h2 id="seo-setup-title" className="mb-2 text-2xl font-semibold text-black">
              Enable Advanced SEO Automation?
            </h2>
            <p className="mb-6 text-base text-black/70">Optimize your WordPress content with:</p>

            <ul className="mb-6 space-y-2" role="list">
              <li className="flex items-center gap-2 text-base text-black">
                <span className="text-green-600" aria-hidden="true">
                  ✓
                </span>
                JSON-LD structured data
              </li>
              <li className="flex items-center gap-2 text-base text-black">
                <span className="text-green-600" aria-hidden="true">
                  ✓
                </span>
                Virtual robots.txt (configure via Rank Math dashboard)
              </li>
              <li className="flex items-center gap-2 text-base text-black">
                <span className="text-green-600" aria-hidden="true">
                  ✓
                </span>
                llms.txt for AI crawlers (configure via Rank Math dashboard)
              </li>
            </ul>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={onSkip}
                className="flex-1"
                aria-label="Skip SEO automation setup"
              >
                No Thanks
              </Button>
              <Button onClick={detectSetup} className="flex-1" aria-label="Enable SEO automation">
                Yes, Enable SEO
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  if (step === 'detecting') {
    return (
      <Modal isOpen={true}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            <div className="flex flex-col items-center py-8" role="status" aria-live="polite">
              <Spinner size={32} aria-hidden="true" />
              <p id="seo-detecting-title" className="mt-4 text-base text-black/70">
                Checking your WordPress setup...
              </p>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  if (step === 'installing') {
    return (
      <Modal isOpen={true}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            <div className="flex flex-col items-center py-8" role="status" aria-live="polite">
              <Spinner size={32} aria-hidden="true" />
              <p id="seo-installing-title" className="mt-4 text-base text-black/70">
                Installing Rank Math SEO...
              </p>
              <p className="mt-2 text-sm text-black/50">This may take 30-60 seconds</p>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  if (step === 'wizard_guidance') {
    return (
      <Modal isOpen={true}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            <h2 id="wizard-guidance-title" className="mb-2 text-2xl font-semibold text-black">
              Rank Math Installed!
            </h2>
            <p className="mb-6 text-base text-black/70">
              Now complete the Rank Math setup wizard to configure SEO automation.
            </p>

            <div className="mb-6 rounded-2xl border-2 border-blue-200 bg-blue-50 p-4">
              <Link
                target="_blank"
                href={`${baseUrl}/wp-admin/admin.php?page=rank-math-wizard`}
                className="block"
              >
                <p className="text-base font-medium text-blue-900">Run Rank Math Setup Wizard</p>
                <p className="mt-2 text-sm text-blue-700">
                  Complete the wizard in your WordPress admin. In Step 3 (Advanced Options), make
                  sure to enable &quot;Advanced Mode&quot; and check &quot;Headless CMS
                  Support&quot;.
                </p>
                <p className="mt-3 text-xs font-medium text-blue-800">
                  Click here to open wizard in new tab →
                </p>
              </Link>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => onComplete(false)}
                className="flex-1"
                aria-label="Skip wizard and continue"
              >
                Skip for Now
              </Button>
              <Button
                onClick={detectSetup}
                className="flex-1"
                aria-label="Continue to check configuration"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  if (step === 'results' || step === 'complete') {
    const isReady = detection?.rankMath.headlessCmsEnabled && detection?.rankMath.llmsTxtEnabled
    const hasRankMath = detection?.rankMath.installed
    const warnings = detection?.warnings || []
    const criticalWarning = warnings.find(
      (w) => w.type === 'firewall_blocking' || w.type === 'configuration_required'
    )

    return (
      <Modal isOpen={true}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            {error ? (
              <>
                <h2 id="seo-results-title" className="mb-2 text-2xl font-semibold text-red-600">
                  Setup Error
                </h2>
                <p className="mb-6 text-base text-black/70" role="alert">
                  {error}
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={onSkip}
                    className="flex-1"
                    aria-label="Skip SEO setup and continue"
                  >
                    Skip SEO Setup
                  </Button>
                  <Button
                    onClick={detectSetup}
                    className="flex-1"
                    aria-label="Retry setup detection"
                  >
                    Retry
                  </Button>
                </div>
              </>
            ) : !hasRankMath ? (
              <>
                <h2 id="seo-results-title" className="mb-2 text-2xl font-semibold text-black">
                  Rank Math Not Installed
                </h2>
                <p className="mb-4 text-base text-black/70">
                  To enable Advanced SEO Automation, you need Rank Math SEO (free plugin).
                </p>

                {detection?.conflicts && detection.conflicts.length > 0 && (
                  <div className="mb-4 rounded-2xl bg-yellow-50 p-4" role="alert">
                    <p className="text-base font-medium text-yellow-800">
                      Conflicting plugins detected:
                    </p>
                    <ul className="mt-2 space-y-1" role="list">
                      {detection.conflicts.map((c) => (
                        <li key={c.slug} className="text-base text-yellow-700">
                          • {c.name} {c.active && '(active)'}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-sm text-yellow-600">
                      These will be deactivated during installation.
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={onSkip}
                    className="flex-1"
                    aria-label="Skip installation and continue"
                  >
                    Skip for Now
                  </Button>
                  {detection?.canAutoInstall && (
                    <Button
                      onClick={installRankMath}
                      className="flex-1"
                      aria-label="Install Rank Math SEO plugin"
                    >
                      Install Rank Math
                    </Button>
                  )}
                </div>

                {!detection?.canAutoInstall && (
                  <p className="mt-4 text-sm text-black/50" role="status">
                    Automatic installation not available. Please install Rank Math manually.
                  </p>
                )}
              </>
            ) : !isReady ? (
              <>
                <h2 id="seo-results-title" className="mb-2 text-2xl font-semibold text-black">
                  Configuration Required
                </h2>
                <p className="mb-4 text-base text-black/70">
                  Rank Math is installed! Enable these features to activate SEO automation:
                </p>

                <div className="mb-6 space-y-3" role="list">
                  <div className="rounded-2xl bg-gray-50 p-4" role="listitem">
                    <Link
                      target="_blank"
                      href={`${baseUrl}/wp-admin/admin.php?page=rank-math-options-general&view=others`}
                      className="block"
                    >
                      <p className="text-base font-medium text-black">
                        <span aria-hidden="true">
                          {detection?.rankMath.headlessCmsEnabled ? '✓' : '○'}
                        </span>{' '}
                        1. Enable Headless CMS Support
                        {detection?.rankMath.headlessCmsEnabled && (
                          <span className="sr-only">(enabled)</span>
                        )}
                      </p>
                      <p className="mt-1 text-sm text-black/70">
                        Go to Rank Math → General Settings → Others → Enable &quot;Headless CMS
                        Support&quot;
                      </p>
                    </Link>
                  </div>

                  <div className="rounded-2xl bg-gray-50 p-4" role="listitem">
                    <Link
                      target="_blank"
                      href={`${baseUrl}/wp-admin/admin.php?page=rank-math`}
                      className="block"
                    >
                      <p className="text-base font-medium text-black">
                        <span aria-hidden="true">
                          {detection?.rankMath.llmsTxtEnabled ? '✓' : '○'}
                        </span>{' '}
                        2. Enable LLMS Txt Module
                        {detection?.rankMath.llmsTxtEnabled && (
                          <span className="sr-only">(enabled)</span>
                        )}
                      </p>
                      <p className="mt-1 text-sm text-black/70">
                        Go to Rank Math → Dashboard → Scroll to &quot;LLMS Txt&quot; and toggle it
                        ON
                      </p>
                    </Link>
                  </div>
                </div>

                {criticalWarning && (
                  <div className="mb-4 rounded-2xl bg-red-50 p-4" role="alert">
                    <p className="text-base font-medium text-red-800">
                      {criticalWarning.type === 'firewall_blocking'
                        ? 'Firewall Blocking Detected'
                        : 'Configuration Issue'}
                    </p>
                    <p className="mt-2 text-sm text-red-700">{criticalWarning.message}</p>
                    {criticalWarning.solution && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-red-800">Solution:</p>
                        <pre className="mt-2 overflow-x-auto rounded-xl bg-red-100 p-3 text-xs text-red-900">
                          {criticalWarning.solution}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => onComplete(false)}
                    className="flex-1"
                    aria-label="Skip configuration and continue without SEO automation"
                  >
                    Skip for Now
                  </Button>
                  <Button
                    onClick={detectSetup}
                    className="flex-1"
                    aria-label="Check configuration again"
                  >
                    Check Again
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div
                  className="mb-6 flex flex-col items-center py-4"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100"
                    aria-label="Success"
                  >
                    <span className="text-3xl text-green-600" aria-hidden="true">
                      ✓
                    </span>
                  </div>
                  <h2 id="seo-results-title" className="mb-2 text-2xl font-semibold text-black">
                    All Set!
                  </h2>
                  <p className="text-center text-base text-black/70">
                    Advanced SEO Automation is now active for your site.
                  </p>
                </div>

                <Button
                  onClick={() => onComplete(true)}
                  className="w-full"
                  aria-label="Complete SEO automation setup"
                >
                  Complete Setup
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
    )
  }

  return null
}
